import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { forTablet, isDesktop, forIE, forDesktop } from '@westwing/ui-kit/breakPoints';
import WestwingLogo from '@westwing/ui-kit/Icons/Westwing';
import { caption1 } from '@westwing/ui-kit/typography';
import { textEllipsisCss } from '../Generic/Text/StyledText';
import HybridLink from '../Generic/Links/HybridLink';
import { VictorQaClasses } from '../../../common/qaIds';
import useClubTab from './useClubTab';

const ClubLink = styled(HybridLink)`
    grid-area: clubtab;
    display: flex;
    position: relative;
    box-shadow: inset 0px -2px 2px 0 rgba(0, 0, 0, 0.18);

    ${forIE`
        -ms-grid-row: 1;
        -ms-grid-column: 2;
    `}

    flex-flow: column;
    align-items: center;
    height: 100%;
    width: 50%;
    background: ${({ theme }) => theme.colors.turquoise100};

    ${forDesktop`
        width: 188px; 
        box-shadow: inset -2px -2px 2px 0 rgba(0,0,0,0.18);
        :hover {
            background:${({ theme }) => theme.colors.turquoise80};
        }
    `}
`;

const LogoText = styled.span`
    ${caption1}
    display: inline-block;
    color: ${({ theme }) => theme.colors.white};
    margin-top: 2px;
    line-height: 16.8px;
    ${textEllipsisCss}
    ${forDesktop`
        margin-top: 4px;
    `}
`;

const HighlightBubble = styled.span`
    font-size: 0.571em;
    letter-spacing: 0.017em;
    width: 12px;
    height: 12px;
    margin-left: 11px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.red90};
    color: ${({ theme }) => theme.colors.white};
    text-align: center;
    line-height: 13px;
    grid-column: 15/18;
    grid-row: 1/2;
    ${forTablet`
        padding: 0px 1px 0px 0px;
    `}
    ${forDesktop`
        padding-right: 0;
    `}
    ${forIE`
        position:absolute;
        right: 0;
        -ms-grid-column: 2;
        -ms-grid-column-span: 1;
        -ms-grid-row: 1;
        -ms-grid-row-span: 2;
    `}
`;

const StyledWestwingLogo = styled(WestwingLogo)<{ $isClubTabHighlighted?: boolean }>`
    fill: ${({ theme }) => theme.colors.white};
    ${({ $isClubTabHighlighted }) => (!$isClubTabHighlighted ? 'margin: auto;' : '')}

    grid-column: 1/-1;
    grid-row: 2/-1;
    height: 12px;

    ${forDesktop`
        height: 12px;
    `}

    ${forIE`
        -ms-grid-column: 1;
        -ms-grid-column-span: 1;
        -ms-grid-row: 2;
        -ms-grid-row-span: 2;
    `}
`;
const ClubLogoAndHighlightGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(18, 5px);
    grid-template-rows: 7px 6px 6px;
    justify-content: center;
    margin-top: 1px;
    position: relative;

    ${forTablet`
        margin-top: 2px;
    `}

    ${forIE`
        display: -ms-grid;
        -ms-grid-rows: 7px 6px 6px;
        -ms-grid-columns: 75px 5px;
        margin-top: 2px;
    `}
`;

interface Props {
    fromBurgerMenu?: boolean;
}

const ClubTab: React.FC<Props> = ({ fromBurgerMenu = false }) => {
    const [isDesktopScreen, setIsDesktopScreen] = useState<boolean>(false);
    const { clubText, clubUrl, isClubTabHighlighted, clickTrackingFn } = useClubTab(fromBurgerMenu);

    useEffect(() => {
        setIsDesktopScreen(isDesktop());
    }, [isDesktopScreen]);

    return (
        <ClubLink
            to={clubUrl}
            target={isDesktopScreen ? '_blank' : '_self'}
            onClick={clickTrackingFn}
            className={VictorQaClasses.headerClubTab}
            testId="club-tab"
        >
            <ClubLogoAndHighlightGrid>
                <StyledWestwingLogo $isClubTabHighlighted={isClubTabHighlighted} />
                {isClubTabHighlighted && <HighlightBubble>5</HighlightBubble>}
            </ClubLogoAndHighlightGrid>
            <LogoText>{clubText}</LogoText>
        </ClubLink>
    );
};

export default ClubTab;
